/*
 * What the fleet noticed, arranged for reading.
 *
 * The control plane raises a notification when a detector sees something and re-arms it when
 * the condition clears. Until now they were reachable only through `GET /admin/notifications`,
 * which is to say: not reachable. This is the judgement half of the page, kept pure so it can
 * be asserted without a browser.
 *
 * ## There is no severity here, on purpose
 *
 * A notification carries an evidence class, not a rank. Inventing red / amber / green would
 * mean deciding which findings matter, which is exactly the judgement the evidence model
 * refuses to make on somebody's behalf — and amber in this product means *unknown*, not
 * *warning*, so a severity palette would collide with the one distinction the design system
 * exists to protect. Open and cleared, and what each rests on. Nothing else.
 */

export interface NotificationLike {
  readonly id: string;
  readonly kind: string;
  readonly subject: string;
  readonly title: string;
  readonly body: string;
  readonly evidence: string;
  readonly vantagePoint: string | null;
  readonly raisedAt: string;
  readonly clearedAt: string | null;
  readonly remediation: { readonly suggestion: string; readonly evidence: string } | null;
}

export interface NotificationsView {
  readonly open: readonly NotificationLike[];
  readonly cleared: readonly NotificationLike[];
  readonly headline: string;
  /**
   * The sentence that stops an empty page reading as a clean estate.
   *
   * Always present, never only when the list is empty. "Nothing is open" is a fact about the
   * detectors that exist, and there are two of them — an agent that stopped talking and a mount
   * whose device is gone. A reader who takes it as "the estate is fine" has been misled by a
   * page that told them the truth, which is the failure this product is most prone to.
   */
  readonly caveat: string;
}

/** Newest first. A person reading this wants what just happened. */
function byRaisedAtDescending(a: NotificationLike, b: NotificationLike): number {
  return Date.parse(b.raisedAt) - Date.parse(a.raisedAt);
}

export function organize(notifications: readonly NotificationLike[]): NotificationsView {
  const open = [...notifications].filter((n) => n.clearedAt === null).sort(byRaisedAtDescending);
  const cleared = [...notifications].filter((n) => n.clearedAt !== null).sort(byRaisedAtDescending);

  const kinds = new Set(notifications.map((n) => n.kind));
  const caveat =
    "This is what the fleet's detectors noticed, not a health check of the estate. Only a few " +
    "conditions are watched for, so an empty list means nothing has been noticed — never that " +
    "nothing is wrong.";

  if (open.length === 0 && cleared.length === 0) {
    return {
      open,
      cleared,
      // Not "everything is fine". Nothing has been raised, which is a statement about the
      // detectors rather than about the machines.
      headline: "Nothing has been raised.",
      caveat,
    };
  }

  if (open.length === 0) {
    return {
      open,
      cleared,
      headline:
        `Nothing open · ${cleared.length} resolved ${cleared.length === 1 ? "notification" : "notifications"} on the record`,
      caveat,
    };
  }

  return {
    open,
    cleared,
    headline:
      `${open.length} open · ` +
      `${kinds.size} ${kinds.size === 1 ? "kind" : "kinds"} of condition · ` +
      `${cleared.length} since resolved`,
    caveat,
  };
}

/**
 * How long a notification has been open, in words a person reads once.
 *
 * Whole units and no seconds: the interesting question is "since yesterday or since a moment
 * ago", and a notification that resolves in seconds is one nobody was going to read.
 */
export function openFor(raisedAt: string, now: Date): string {
  const ms = now.getTime() - Date.parse(raisedAt);
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/**
 * The label a remediation is shown under.
 *
 * Composed here rather than written into the page so the class cannot be dropped by a layout
 * change. CLAUDE.md §3 rule 2: a surface that cannot express provenance is a defective surface,
 * and advice is the place where dropping it would matter most — an operator acting on a
 * suggestion should know it is a suggestion.
 */
export function remediationLabel(remediation: { readonly evidence: string } | null): string | null {
  return remediation === null ? null : `Suggested — ${remediation.evidence}, not established`;
}

/**
 * Where a notification is acted on.
 *
 * A notification that names a fault but not the place to fix it is a dead end — the remediation
 * prose already NAMES "the Agents page" but could not be clicked. Keyed on the detector's own
 * `kind`, whose constants live in `services/control-plane/src/notifier-*.ts`
 * (`AGENT_QUIET_KIND`, `VERSION_DRIFT_KIND`, `STALE_MOUNT_KIND`, `REENUMERATION_KIND`,
 * `BACKUP_FAILED_KIND` = "action-failed", `ENDPOINT_SILENT_KIND`). An unknown kind returns null
 * rather than a wrong link:
 * a link to the wrong page is worse than none.
 */
const KIND_DESTINATIONS: Record<string, { readonly href: string; readonly label: string }> = {
  "agent-quiet": { href: "/agents", label: "Agents" },
  "agent-version-drift": { href: "/agents", label: "Agents" },
  "stale-mount": { href: "/discovery", label: "Discovery" },
  "usb-reenumeration": { href: "/discovery", label: "Discovery" },
  "action-failed": { href: "/backups", label: "Backups" },
  // Live, not Discovery: the finding is that something stopped answering a runtime check, which
  // is the question Live answers. Discovery is where the estate was found, not how it is now.
  "endpoint-silent": { href: "/live", label: "Live" },
};

export function destinationForKind(
  kind: string,
): { readonly href: string; readonly label: string } | null {
  return KIND_DESTINATIONS[kind] ?? null;
}

const SUBJECT_SEP = " ";
function subjectKey(n: Pick<NotificationLike, "kind" | "subject">): string {
  return `${n.kind}${SUBJECT_SEP}${n.subject}`;
}

/**
 * How many times this exact condition (kind + subject) appears across the whole record — open and
 * cleared. "This broke four times last week and fixed itself each time" is the shape of a real
 * fault, and a page showing each occurrence as an unrelated card cannot say it.
 */
export function recurrenceCount(
  notification: Pick<NotificationLike, "kind" | "subject">,
  all: readonly NotificationLike[],
): number {
  const key = subjectKey(notification);
  return all.filter((n) => subjectKey(n) === key).length;
}

export interface ClearedGroup {
  /** The most recent cleared occurrence — what the collapsed row renders. */
  readonly representative: NotificationLike;
  /** How many cleared occurrences of this exact (kind, subject) there are. */
  readonly count: number;
}

/**
 * Collapse repeated cleared notifications of the same (kind, subject) into one row carrying a
 * count, so a condition that came and went five times reads as one line — "resolved, seen 5×" —
 * rather than five near-identical cards. Newest representative first.
 */
export function groupCleared(cleared: readonly NotificationLike[]): ClearedGroup[] {
  const groups = new Map<string, NotificationLike[]>();
  for (const n of cleared) {
    const key = subjectKey(n);
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [n]);
    else list.push(n);
  }
  return [...groups.values()]
    .map((list) => {
      const sorted = [...list].sort(byRaisedAtDescending);
      return { representative: sorted[0]!, count: sorted.length };
    })
    .sort((a, b) => byRaisedAtDescending(a.representative, b.representative));
}

/**
 * Whether the sidebar shows a dot.
 *
 * A page nobody thinks to visit is not a surface. The whole point of the arc this belongs to was
 * that the next fault of that shape should be a notification rather than somebody wondering why
 * an application is broken — and that fails just as completely if the notification sits on a page
 * behind a nav item nobody has reason to click.
 *
 * ## Silent when it does not know, and why that is defensible here
 *
 * A failed fetch, a guest session, or a deployment with notifications switched off all produce
 * no dot — the same as "nothing is open". Everywhere else in this product that conflation is the
 * error to avoid, so it is worth saying why it is right here and only here:
 *
 * The nav is a **courtesy**, not a claim. Its absence asserts nothing; it is the page that has to
 * distinguish "nothing open" from "could not ask" from "nothing is watching", and it does. A dot
 * that appeared on failure would send people to a page to look at nothing, and an error rendered
 * in the sidebar of every page in the viewer would be read once and ignored forever.
 *
 * The rule is the same one that governs hiding a control in a UI: a courtesy may be silent, a
 * claim may not.
 */
export function shouldFlagNav(result: { ok: true; open: number } | { ok: false }): boolean {
  return result.ok && result.open > 0;
}
